// CHUNK 17: Saved charts library at /meus-mapas
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface SavedChart {
  id:                string;
  nickname:          string;
  relationship_type: string;
  birth_date:        string;
  birth_city:        string | null;
  sun_sign:          string | null;
  moon_sign:         string | null;
  ascendant_sign:    string | null;
  created_at:        string;
}

const REL_EMOJI: Record<string, string> = {
  romantic:     '💕',
  family:       '✨',
  friend:       '🤝',
  professional: '💼',
  other:        '✦',
};

function AddChartModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState({
    nickname:          '',
    relationship_type: 'other',
    birth_date:        '',
    birth_time:        '',
    birth_city:        '',
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.nickname || !form.birth_date) return;
    setLoading(true);
    setError('');
    try {
      const { error: e } = await supabase.from('saved_charts').insert({
        nickname:          form.nickname,
        relationship_type: form.relationship_type,
        birth_date:        form.birth_date,
        birth_time:        form.birth_time || null,
        birth_city:        form.birth_city || null,
      });
      if (e) throw e;
      onSuccess();
    } catch (err) {
      setError('Não foi possível salvar. Tente novamente.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0D1130] border border-[#C9A84C]/30 rounded-sm w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-[#C9A84C] text-sm uppercase tracking-[2px]">Salvar mapa</h2>
          <button onClick={onClose} className="text-[#F5ECD7]/40 hover:text-[#F5ECD7]">×</button>
        </div>

        {error && <div className="mb-4 p-3 border border-red-500/40 text-red-300 text-xs rounded-sm">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          {([
            { key: 'nickname',   label: 'Apelido',               type: 'text', placeholder: 'Ex: Mãe, João, Amiga Maria', required: true },
            { key: 'birth_date', label: 'Data de nascimento',    type: 'date', placeholder: '',                           required: true },
            { key: 'birth_time', label: 'Hora de nascimento',    type: 'time', placeholder: '',                           required: false },
            { key: 'birth_city', label: 'Cidade de nascimento',  type: 'text', placeholder: 'Cidade, Estado',             required: false },
          ] as const).map(f => (
            <label key={f.key} className="block">
              <span className="block text-[#F5ECD7]/60 text-xs uppercase tracking-wider mb-2">{f.label}</span>
              <input
                type={f.type}
                value={form[f.key]}
                onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                required={f.required}
                className="w-full bg-transparent border border-[#C9A84C]/30 text-[#F5ECD7] p-3 rounded-sm placeholder-[#F5ECD7]/30 focus:border-[#C9A84C] focus:outline-none"
              />
            </label>
          ))}

          <label className="block">
            <span className="block text-[#F5ECD7]/60 text-xs uppercase tracking-wider mb-2">Tipo</span>
            <select
              value={form.relationship_type}
              onChange={e => setForm(p => ({ ...p, relationship_type: e.target.value }))}
              className="w-full bg-[#0D1130] border border-[#C9A84C]/30 text-[#F5ECD7] p-3 rounded-sm focus:border-[#C9A84C] focus:outline-none"
            >
              {[
                ['romantic',     '💕 Romântico'],
                ['family',       '✨ Familiar'],
                ['friend',       '🤝 Amizade'],
                ['professional', '💼 Profissional'],
                ['other',        '✦ Outro'],
              ].map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-[#C9A84C] text-[#0D1130] text-xs uppercase tracking-[2px] font-bold hover:bg-[#C9A84C]/90 disabled:opacity-60 transition-colors"
          >
            {loading ? 'Salvando…' : 'Salvar mapa'}
          </button>
        </form>
      </div>
    </div>
  );
}

export function SavedChartsPage() {
  const navigate           = useNavigate();
  const [charts, setCharts] = useState<SavedChart[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('saved_charts')
      .select('*')
      .order('created_at', { ascending: false });
    setCharts((data ?? []) as SavedChart[]);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(id: string) {
    if (!confirm('Remover este mapa salvo?')) return;
    await supabase.from('saved_charts').delete().eq('id', id);
    setCharts(prev => prev.filter(c => c.id !== id));
  }

  return (
    <div className="min-h-screen bg-[#0D1130] py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-[#C9A84C] text-2xl font-light tracking-wider">✦ Meus Mapas Salvos</h1>
            <p className="text-[#F5ECD7]/50 text-sm mt-1">Acesso rápido para criar sinastrias</p>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="px-5 py-2.5 bg-[#C9A84C] text-[#0D1130] text-xs uppercase tracking-[2px] font-bold hover:bg-[#C9A84C]/90 transition-colors"
          >
            + Adicionar
          </button>
        </div>

        {loading && <div className="text-[#F5ECD7]/50 text-sm">Carregando…</div>}

        {!loading && charts.length === 0 && (
          <div className="text-center py-16 border border-[#C9A84C]/20 rounded-sm">
            <div className="text-[#C9A84C] text-3xl mb-4">✦</div>
            <p className="text-[#F5ECD7]/60 mb-2">Nenhum mapa salvo ainda</p>
            <p className="text-[#F5ECD7]/40 text-sm mb-6">
              Salve mapas de pessoas importantes para criar sinastrias com um clique
            </p>
            <button onClick={() => setShowAdd(true)} className="px-6 py-3 bg-[#C9A84C] text-[#0D1130] text-xs uppercase tracking-[2px] font-bold hover:bg-[#C9A84C]/90">
              Salvar primeiro mapa
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {charts.map(c => (
            <div key={c.id} className="border border-[#C9A84C]/20 rounded-sm p-5 hover:border-[#C9A84C]/40 transition-colors">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-[#F5ECD7] font-medium">{c.nickname}</h3>
                  <span className="text-[#F5ECD7]/50 text-xs">
                    {REL_EMOJI[c.relationship_type] ?? '✦'} {c.relationship_type}
                  </span>
                </div>
                <button
                  onClick={() => handleDelete(c.id)}
                  aria-label="Remover"
                  className="text-[#F5ECD7]/30 hover:text-red-400 text-lg transition-colors"
                >
                  ×
                </button>
              </div>

              <div className="text-[#F5ECD7]/60 text-xs space-y-1 mb-4">
                <div>Nascimento: {new Date(c.birth_date).toLocaleDateString('pt-BR')}</div>
                {c.birth_city      && <div>Cidade: {c.birth_city}</div>}
                {c.sun_sign        && <div>☉ Sol: {c.sun_sign}</div>}
                {c.moon_sign       && <div>☽ Lua: {c.moon_sign}</div>}
                {c.ascendant_sign  && <div>↑ Asc: {c.ascendant_sign}</div>}
              </div>

              <button
                onClick={() => navigate(`/sinastria/criar?savedChart=${c.id}`)}
                className="w-full py-2.5 border border-[#C9A84C]/30 text-[#C9A84C] text-xs uppercase tracking-wider hover:bg-[#C9A84C]/10 transition-colors"
              >
                Criar sinastria
              </button>
            </div>
          ))}
        </div>
      </div>

      {showAdd && (
        <AddChartModal
          onClose={() => setShowAdd(false)}
          onSuccess={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}
